// src/components/VisitorCounter.tsx
import { motion, AnimatePresence } from 'framer-motion';
import { Users, AlertCircle } from 'lucide-react';
import { useVisitorCount } from '../../hooks/userVisitorCount';

interface VisitorCounterProps {
  type: 'project' | 'total';
  projectId?: string;
  className?: string;
}

export const VisitorCounter: React.FC<VisitorCounterProps> = ({
  type,
  projectId,
  className = '',
}) => {
  const { count, isLoading, error } = useVisitorCount({ type, projectId });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
      className={`flex items-center gap-2 px-3 py-1.5 
        bg-white/90 dark:bg-gray-700/90 rounded-lg
        shadow-sm border border-gray-200 dark:border-gray-600
        transition-all duration-300 ease-in-out
        ${className}`}
    >
      <Users
        size={16}
        className={`text-gray-500 dark:text-gray-400 transition-all duration-300
          ${isLoading ? 'animate-pulse' : ''}`}
      />
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: 'auto' }}
            exit={{ opacity: 0, width: 0 }}
            className="h-4 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden"
          >
            <div
              className="w-16 h-full bg-gradient-to-r from-gray-200 to-gray-300 
              dark:from-gray-600 dark:to-gray-700 animate-pulse"
            />
          </motion.div>
        ) : error ? (
          <motion.div
            key="error"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="flex items-center gap-1 text-red-500 dark:text-red-400"
          >
            <AlertCircle size={14} className="animate-pulse" />
            <span className="text-sm">Error</span>
          </motion.div>
        ) : (
          <motion.span
            key={count}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm font-medium text-gray-600 dark:text-gray-300"
          >
            {count.toLocaleString()}
            <span className="ml-1 opacity-75">
              {type === 'total' ? 'total views' : 'views'}
            </span>
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
